import React, { useState, useEffect } from 'react';

const Gallery = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Mock data for demonstration - in a real app, this would come from a backend
  const [items] = useState([
    {
      id: 1,
      type: 'image',
      url: 'https://cdn.usegalileo.ai/stability/b8b8b8b8-b8b8-b8b8-b8b8-b8b8b8b8b8b8.png',
      prompt: 'Interstellar travel, a black hole, from which a nearly shattered vintage train bursts forth',
      createdAt: '2024-01-15T10:30:00Z',
      tags: ['space', 'train', 'cinematic']
    },
    {
      id: 2,
      type: 'image',
      url: 'https://cdn.usegalileo.ai/stability/c8c8c8c8-c8c8-c8c8-c8c8-c8c8c8c8c8c8.png',
      prompt: 'Generate a series of 4 coherent illustrations focusing on the same corner of a courtyard across the four seasons',
      createdAt: '2024-01-14T15:45:00Z',
      tags: ['seasons', 'courtyard', 'illustration']
    },
    {
      id: 3,
      type: 'video',
      url: 'https://example.com/video1.mp4',
      thumbnail: 'https://cdn.usegalileo.ai/stability/d8d8d8d8-d8d8-d8d8-d8d8-d8d8d8d8d8d8.png',
      prompt: 'At breakneck speed, drones thread through intricate obstacles or stunning natural wonders',
      createdAt: '2024-01-13T09:20:00Z',
      tags: ['drone', 'speed', 'nature']
    },
    {
      id: 4,
      type: 'image',
      url: 'https://cdn.usegalileo.ai/stability/e8e8e8e8-e8e8-e8e8-e8e8-e8e8e8e8e8e8.png',
      prompt: 'Generate a close-up image of a dog lying on lush grass',
      createdAt: '2024-01-12T14:10:00Z',
      tags: ['dog', 'nature', 'closeup']
    },
    {
      id: 5,
      type: 'image',
      url: 'https://cdn.usegalileo.ai/stability/f8f8f8f8-f8f8-f8f8-f8f8-f8f8f8f8f8f8.png',
      prompt: 'Using this LOGO as a reference, create a visual design system for an outdoor sports brand',
      createdAt: '2024-01-11T11:55:00Z',
      tags: ['logo', 'design', 'sports', 'branding']
    },
    {
      id: 6,
      type: 'video',
      url: 'https://example.com/video2.mp4',
      thumbnail: 'https://cdn.usegalileo.ai/stability/a8a8a8a8-a8a8-a8a8-a8a8-a8a8a8a8a8a8.png',
      prompt: 'Create a 360-degree orbiting camera shot based on this photo',
      createdAt: '2024-01-10T16:30:00Z',
      tags: ['360', 'camera', 'orbit']
    }
  ]);

  const filteredItems = items.filter(item => {
    const matchesFilter = filter === 'all' || item.type === filter;
    const matchesSearch = item.prompt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="px-40 flex flex-1 justify-center py-5">
      <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
        <div className="flex flex-col gap-6 px-4 py-6">
          <div className="flex flex-col gap-4">
            <h1 className="text-white text-[32px] font-bold leading-tight tracking-[-0.015em]">
              Gallery
            </h1>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex flex-1">
                <label className="flex flex-col min-w-40 flex-1">
                  <div className="flex w-full flex-1 items-stretch rounded-xl h-full">
                    <div
                      className="text-[#a69db9] flex border-none bg-[#2e2839] items-center justify-center pl-4 rounded-l-xl border-r-0"
                      data-icon="MagnifyingGlass"
                      data-size="24px"
                      data-weight="regular"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" fill="currentColor" viewBox="0 0 256 256">
                        <path
                          d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"
                        />
                      </svg>
                    </div>
                    <input
                      placeholder="Search by prompt or tags..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] focus:border-none h-14 placeholder:text-[#a69db9] px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal"
                    />
                  </div>
                </label>
              </div>
              
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="form-input flex resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] px-4 text-base font-normal leading-normal min-w-32"
              >
                <option value="all">All</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-[#a69db9] text-sm">
                {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} found
              </p>
              <div className="flex gap-2">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2e2839] text-white text-sm hover:bg-[#3a3545]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" fill="currentColor" viewBox="0 0 256 256">
                    <path d="M32,64a8,8,0,0,1,8-8H216a8,8,0,0,1,0,16H40A8,8,0,0,1,32,64Zm8,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16Zm176,48H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z"/>
                  </svg>
                  List
                </button>
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#5b13ec] text-white text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" fill="currentColor" viewBox="0 0 256 256">
                    <path d="M104,40H56A16,16,0,0,0,40,56v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V56A16,16,0,0,0,104,40Zm0,64H56V56h48v48Zm96-64H152a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V56A16,16,0,0,0,200,40Zm0,64H152V56h48v48ZM104,136H56a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V152A16,16,0,0,0,104,136Zm0,64H56V152h48v48Zm96-64H152a16,16,0,0,0-16,16v48a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V152A16,16,0,0,0,200,136Zm0,64H152V152h48v48Z"/>
                  </svg>
                  Grid
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <div key={item.id} className="flex flex-col gap-3 bg-[#1c1a21] border border-[#2e2839] rounded-xl p-4 hover:border-[#5b13ec] transition-colors">
                  <div className="relative">
                    {item.type === 'image' ? (
                      <div
                        className="aspect-square bg-cover bg-center bg-no-repeat rounded-lg"
                        style={{
                          backgroundImage: `url("${item.url}")`
                        }}
                      />
                    ) : (
                      <div className="relative">
                        <div
                          className="aspect-square bg-cover bg-center bg-no-repeat rounded-lg"
                          style={{
                            backgroundImage: `url("${item.thumbnail}")`
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" fill="white" viewBox="0 0 256 256">
                              <path d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.type === 'image' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <p className="text-white text-sm font-medium line-clamp-2">
                      {item.prompt}
                    </p>
                    <p className="text-[#a69db9] text-xs">
                      {formatDate(item.createdAt)}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {item.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-[#2e2839] text-[#a69db9] text-xs rounded">
                          #{tag}
                        </span>
                      ))}
                      {item.tags.length > 3 && (
                        <span className="px-2 py-1 bg-[#2e2839] text-[#a69db9] text-xs rounded">
                          +{item.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <button className="flex-1 px-3 py-2 bg-[#5b13ec] text-white text-sm rounded-lg hover:bg-[#4a0fd1] transition-colors">
                      View
                    </button>
                    <button className="px-3 py-2 bg-[#2e2839] text-white text-sm rounded-lg hover:bg-[#3a3545] transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M216,112v96a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V112A16,16,0,0,1,56,96H80a8,8,0,0,1,0,16H56v96H200V112H176a8,8,0,0,1,0-16h24A16,16,0,0,1,216,112ZM93.66,69.66,120,43.31V128a8,8,0,0,0,16,0V43.31l26.34,26.35a8,8,0,0,0,11.32-11.32l-40-40a8,8,0,0,0-11.32,0l-40,40A8,8,0,0,0,93.66,69.66Z"/>
                      </svg>
                    </button>
                    <button className="px-3 py-2 bg-[#2e2839] text-white text-sm rounded-lg hover:bg-[#3a3545] transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,163.18V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-[#a69db9] mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48px" height="48px" fill="currentColor" viewBox="0 0 256 256">
                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,163.18V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                  </svg>
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">No items found</h3>
                <p className="text-[#a69db9] text-sm">
                  {searchTerm ? 'Try adjusting your search terms' : 'Start creating some images and videos to see them here'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gallery;